import React, { useState, useRef, useEffect, useMemo } from "react";
import { ChevronDown, Check, Search, X } from "lucide-react";

interface SearchableDropdownOption {
  label: string;
  value: string;
}

interface SearchableDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: SearchableDropdownOption[];
  placeholder?: string;
  maxVisibleItems?: number;
  disabled?: boolean;
  error?: boolean;
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  value,
  onChange,
  options,
  placeholder = "Select an option",
  maxVisibleItems = 6,
  disabled = false,
  error = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const optionsContainerRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  /* Smart positioning state */
  const [position, setPosition] = useState<"bottom" | "top">("bottom");

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [options, searchQuery]);

  const selectedOption = options.find((opt) => opt.value === value);

  // Reset focused index when filtered options change
  useEffect(() => {
    setFocusedIndex(-1);
  }, [filteredOptions]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Calculate position and focus search input when dropdown opens
  useEffect(() => {
    if (isOpen) {
      if (dropdownRef.current) {
        const rect = dropdownRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const requiredSpace = Math.min(
          filteredOptions.length * 40 + 50, // Approx height calculation
          200 + 50, // Max height + padding
        );

        if (spaceBelow < requiredSpace && rect.top > requiredSpace) {
          setPosition("top");
        } else {
          setPosition("bottom");
        }
      }

      if (inputRef.current) {
        // Small delay to ensure render
        setTimeout(() => inputRef.current?.focus(), 50);
      }
    } else {
      setFocusedIndex(-1);
    }
  }, [isOpen, filteredOptions.length]);

  // Scroll focused option into view
  useEffect(() => {
    if (focusedIndex >= 0 && optionRefs.current[focusedIndex]) {
      optionRefs.current[focusedIndex]?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [focusedIndex]);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchQuery("");
    setFocusedIndex(-1);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("");
    setSearchQuery("");
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setFocusedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : prev,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setFocusedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case "Enter":
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < filteredOptions.length) {
          handleSelect(filteredOptions[focusedIndex].value);
        } else if (filteredOptions.length > 0) {
          handleSelect(filteredOptions[0].value);
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        setFocusedIndex(-1);
        break;
      default:
        break;
    }
  };

  const maxHeight = `calc(${maxVisibleItems} * 2.5rem)`;

  const dropdownClasses = `absolute left-0 right-0 z-[9999] bg-white border-2 border-orange-400 rounded-lg shadow-xl animate-in fade-in duration-150 ${
    position === "top"
      ? "bottom-full mb-2 slide-in-from-bottom-2"
      : "top-full mt-2 slide-in-from-top-2"
  }`;

  return (
    <div ref={dropdownRef} className="relative w-full">
      {/* Dropdown Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full px-3 py-2 rounded-lg transition-all duration-200 flex items-center justify-between text-sm font-medium border-2 ${
          error
            ? "border-red-500 focus:border-red-600 focus:ring-2 focus:ring-red-200"
            : disabled
              ? "border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50"
              : isOpen
                ? "border-orange-500 focus:ring-2 focus:ring-orange-200 bg-white"
                : "border-gray-300 text-gray-700 hover:border-orange-400 focus:border-orange-500 focus:ring-2 focus:ring-orange-100 bg-white"
        }`}
      >
        <div className="flex items-center gap-2 flex-1 text-left">
          <span className={selectedOption ? "text-gray-800" : "text-gray-500"}>
            {selectedOption?.label || placeholder}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {selectedOption && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="text-gray-400 hover:text-red-500 transition-colors p-1 -mr-1"
              aria-label="Clear selection"
              title="Clear selection"
            >
              <X size={16} />
            </button>
          )}
          <ChevronDown
            size={18}
            className={`text-gray-600 transition-transform duration-300 flex-shrink-0 ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className={dropdownClasses}>
          {/* Search Input */}
          <div
            className={`p-3 border-gray-200 bg-white ${
              position === "top"
                ? "border-t rounded-b-lg order-2"
                : "border-b rounded-t-lg"
            }`}
          >
            <div className="relative">
              <Search
                size={16}
                className="absolute left-3 top-3 text-gray-400"
              />
              <input
                ref={inputRef}
                type="text"
                placeholder="Search options..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100 transition-colors"
                onKeyDown={handleKeyDown}
              />
            </div>
          </div>

          {/* Options List */}
          {/* eslint-disable-next-line react/style-prop-object */}
          <div
            ref={optionsContainerRef}
            style={{ maxHeight, overflowY: "auto" }}
            className={`py-1 ${position === "top" ? "order-1" : ""}`}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => {
                const isSelected = value === option.value;
                const isFocused = focusedIndex === index;
                return (
                  <button
                    key={option.value}
                    ref={(el) => {
                      optionRefs.current[index] = el;
                    }}
                    type="button"
                    onClick={() => handleSelect(option.value)}
                    onMouseEnter={() => setFocusedIndex(index)}
                    className={`w-full px-4 py-2.5 text-sm font-medium flex items-center justify-between transition-all duration-150 border-l-4 ${
                      isSelected
                        ? "bg-orange-50 text-orange-700 border-l-orange-500"
                        : isFocused
                          ? "bg-orange-50 text-orange-700 border-l-orange-300"
                          : "text-gray-700 hover:bg-orange-50 border-l-transparent hover:border-l-orange-300"
                    }`}
                  >
                    <span className="flex-1 text-left">{option.label}</span>
                    {isSelected && (
                      <Check
                        size={18}
                        className="text-blue-500 flex-shrink-0"
                      />
                    )}
                  </button>
                );
              })
            ) : (
              <div className="px-4 py-8 text-center text-gray-500 text-sm">
                No options found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableDropdown;
